/**
 * Shared types for context menu actions
 */

import { Datum, Filetree } from "../datastructure";
import { RenderableData } from "../fetchFiles";

/**
 * Context for executing an action - provides access to tree state and callbacks
 */
export interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

export interface ActionContext {
  /** Refresh the file tree data */
  refreshTree: () => void;
  /** Show a toast notification */
  showToast?: (message: string, type: "success" | "error" | "info") => void;
  /** Show a confirmation modal and return true if confirmed */
  showConfirmation?: (options: ConfirmationOptions) => Promise<boolean>;
  /** The current drive ID (if in single-drive mode) */
  driveId?: string;
  /** The current organization ID */
  orgId?: string;
  // This org's read-only client drive names, served per-org (#10972).
  readOnlyDriveNames?: readonly string[];
  sftpDriveNames?: readonly string[];
  // This org's named SharePoint site roots, served per-org (#11713).
  sharepointRootNames?: readonly string[];
  /** Whether in across-drives mode (first path segment is synthetic drive name) */
  isAcrossDrivesMode?: boolean;
  /** The filetree instance for accessing tree state */
  filetree?: Filetree;
  /**
   * Called when an async sort has been enqueued, with the Celery task id and
   * the number of files being sorted. The filetree hook uses this to drive the
   * persistent "Sorting X of N…" progress indicator and poll for completion.
   * Absent on surfaces without a progress channel (e.g. selection modals).
   */
  onSortStarted?: (taskId: string, total: number) => void;
}

/**
 * Configuration for a context menu action
 */
export interface ActionConfig {
  /** Unique action identifier */
  action: string;
  /** Display label for the menu item */
  label: string;
  /** Icon component (optional) */
  icon?: React.ReactNode;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Keyboard shortcut hint (optional) */
  shortcut?: string;
  /** Add separator after this item */
  separator?: boolean;
}

/**
 * Function to determine if an action is applicable to a datum
 */
export type ActionFilter = (datum: Datum, context?: ActionContext) => boolean;

/**
 * Function to execute an action
 */
export type ActionExecutor = (
  datum: Datum,
  context: ActionContext
) => Promise<void>;

/**
 * Complete action definition
 */
export interface ActionDefinition {
  config: ActionConfig;
  /** Returns true if this action should be shown for the given datum */
  isApplicable: ActionFilter;
  /** Executes the action */
  execute: ActionExecutor;
}

/**
 * Helper to extract file payload from datum
 */
export function getFilePayload(
  datum: Datum
): (RenderableData & { type: "file" }) | null {
  if (
    datum.type !== "file" ||
    !datum.payload ||
    datum.payload.type !== "file"
  ) {
    return null;
  }
  return datum.payload;
}

/**
 * Helper to get the file path as segments (for API calls). Never joined: a
 * name can contain "/".
 */
export function getFilePath(datum: Datum): string[] {
  return [...datum.path];
}

/**
 * Helper to convert a UI path to an API path.
 * In across-drives mode WITH drives enabled, the first path segment is a
 * synthetic driveId that should be stripped before sending to the API.
 * When drives are disabled, paths don't have a drive prefix even if
 * isAcrossDrivesMode is true.
 */
export function toApiPath(path: string[], filetree?: Filetree): string[] {
  return filetree?.shouldStripDrivePrefix ? path.slice(1) : path;
}
